import { formatDelayMs } from "kontrolEdge/components/ResourceManager/Topology/utils/latencyMatrix";
import {
  SiteLatencyLookup,
  latencySiteOf,
  lowestRttUs,
} from "kontrolEdge/components/SiteLatencyMatrix/siteLatencyLookup";
import {
  OverviewCloudLink,
  OverviewCpe,
  OverviewCpeLegLatency,
  OverviewCpeUplink,
  OverviewSite,
  OverviewSiteLink,
} from "../Types";
import { ServiceHealth } from "./overviewModel";

/**
 * What the Overview map draws, computed from the overview endpoint's `sites`
 * for whatever tenant scope is selected (CTX-868). Pure, so the rules — what
 * merges into a metro, what a link says — are tested apart from the SVG.
 */

/** Sites closer than this are drawn as one metro pin. */
export const METRO_RADIUS_KM = 40;

export interface MapNode {
  /** Stable key: the site id, or the sorted ids of a metro's sites. */
  key: string;
  /** Full name(s), for the expanded card. */
  label: string;
  detail: string;
  /** One-line pill: "KIO QRO", or "KIO MEX2 +1" for a group. */
  shortLabel: string;
  ports: number;
  /** More than one means the pin stands for a group, and says so. */
  siteCount: number;
  lat: number;
  lng: number;
  /** The sites this pin stands for. */
  siteIds: string[];
  health: ServiceHealth;
  /** Every site of the pin is only where a CPE's Backhaul lands. */
  backhaulOnly: boolean;
}

/** What links and clouds need of a node: a map pin or a diagram card alike. */
export type TopologyNode = Pick<MapNode, "key" | "siteIds">;

export interface MapLink {
  key: string;
  from: string;
  to: string;
  label: string;
  /**
   * RTT between the two ends (CTX-843): the lowest among the DCIs the line
   * stands for. Null when the backbone does not measure any of them, and the
   * caption then says nothing about latency; undefined when no latency was
   * asked for (a tenant the endpoint refuses).
   */
  rttUs?: number | null;
}

/** The backbone's site latency, as the map and the diagram read it (CTX-843). */
export interface MapLatency {
  lookup: SiteLatencyLookup;
  /** When the IS-IS snapshot was taken, for the legend. */
  measuredAt: string | null;
}

export interface MapCloud {
  cloud: string;
  services: number;
  /** Map nodes with at least one service reaching this cloud. */
  from: { node: string; health: ServiceHealth }[];
}

type PlacedSite = OverviewSite & { lat: number; lng: number };

const HEALTH_RANK: Record<ServiceHealth, number> = { down: 3, warning: 2, unknown: 1, up: 0 };

function worst(healths: ServiceHealth[]): ServiceHealth {
  return healths.reduce<ServiceHealth>((acc, health) => (HEALTH_RANK[health] > HEALTH_RANK[acc] ? health : acc), "up");
}

/** Great-circle distance in km. */
export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "KIO MEX2 (Santa Fe)" → "KIO MEX2": the part before the location in brackets. */
export function shortSiteName(displayName: string): string {
  return displayName.replace(/\s*\(.*\)\s*$/, "").trim() || displayName;
}

export function isPlaced(site: OverviewSite): site is PlacedSite {
  return site.lat !== null && site.lng !== null;
}

/**
 * One node per placed site, with sites within METRO_RADIUS_KM of each other
 * merged into a metro: three pins on top of each other in Mexico City read as
 * one blob anyway, and their labels would overlap.
 *
 * Two sites joined by a DCI never merge, however close: the DCI is what the
 * map is there to show, and inside one pin it would vanish (Santa Fe and
 * Toluca are ~35 km apart).
 */
export function buildMapNodes(
  sites: OverviewSite[],
  healthBySite: Record<string, ServiceHealth>,
  links: Pick<OverviewSiteLink, "from" | "to">[] = [],
  /**
   * With a projection, sites merge only when their pins would overlap on
   * screen (`radiusPx`), so zooming in always splits a group. Without one, the
   * geographic METRO_RADIUS_KM applies.
   */
  screen?: { project: (lng: number, lat: number) => [number, number]; radiusPx: number }
): MapNode[] {
  const placed = sites.filter(isPlaced);
  const linked = new Set(links.flatMap((link) => [`${link.from}|${link.to}`, `${link.to}|${link.from}`]));
  const close = (a: PlacedSite, b: PlacedSite) => {
    if (!screen) return distanceKm(a, b) <= METRO_RADIUS_KM;
    const [ax, ay] = screen.project(a.lng, a.lat);
    const [bx, by] = screen.project(b.lng, b.lat);
    return Math.hypot(ax - bx, ay - by) <= screen.radiusPx;
  };
  const canMerge = (a: PlacedSite, b: PlacedSite) => close(a, b) && !linked.has(`${a.datacenterId}|${b.datacenterId}`);
  const groups: PlacedSite[][] = [];
  for (const site of placed) {
    const near = groups.find((group) => group.every((member) => canMerge(member, site)));
    if (near) near.push(site);
    else groups.push([site]);
  }

  return groups.map((group) => {
    const siteIds = group.map((site) => site.datacenterId).sort();
    const ports = group.reduce((sum, site) => sum + site.ports, 0);
    const health = worst(group.map((site) => healthBySite[site.datacenterId] ?? "up"));
    const lat = group.reduce((sum, site) => sum + site.lat, 0) / group.length;
    const lng = group.reduce((sum, site) => sum + site.lng, 0) / group.length;
    const backhaulOnly = group.every((site) => site.backhaulOnly === true);
    const portsDetail = backhaulOnly ? "Backhaul landing" : plural(ports, "port", "ports");

    if (group.length === 1) {
      const [site] = group;
      return {
        key: site.datacenterId,
        label: site.displayName,
        detail: [site.city, portsDetail].filter(Boolean).join(" · "),
        shortLabel: shortSiteName(site.displayName),
        ports,
        siteCount: 1,
        lat,
        lng,
        siteIds,
        health,
        backhaulOnly,
      };
    }

    // A metro pin names the sites it holds, not "+1": the reader should not have
    // to guess which datacenters are inside. Short names ("KIO MEX2") keep it to
    // one line; past three it says how many more.
    const names = group.map((site) => shortSiteName(site.displayName));
    const label = names.length <= 3 ? names.join(" · ") : `${names.slice(0, 2).join(" · ")} +${names.length - 2}`;
    const cities = new Set(group.map((site) => site.city).filter(Boolean));
    const [onlyCity] = cities;
    return {
      key: siteIds.join("+"),
      label,
      detail: `${cities.size === 1 && onlyCity ? `${onlyCity} metro` : "Metro area"} · ${portsDetail}`,
      shortLabel: `${names[0]} +${names.length - 1}`,
      ports,
      siteCount: group.length,
      lat,
      lng,
      siteIds,
      health,
      backhaulOnly,
    };
  });
}

// ---------------------------------------------------------------------------
// CPEs
// ---------------------------------------------------------------------------

/** A customer's CPE on the map: its own pin, never merged with our sites. */
export interface CpePin {
  /** `cpe:<id>`, apart from the site keys. */
  key: string;
  id: number;
  lat: number;
  lng: number;
  /** One-line pill: the location name, else the CPE name. */
  shortLabel: string;
  /** The expanded card: the CPE name, then its id and type, then where it is. */
  label: string;
  detail: string;
  place: string | null;
  uplinks: OverviewCpeUplink[];
}

export type PlacedCpe = OverviewCpe & { lat: number; lng: number };

export function isCpePlaced(cpe: OverviewCpe): cpe is PlacedCpe {
  return cpe.lat !== null && cpe.lng !== null;
}

/** The placed CPEs as pins, in the order given. */
export function buildCpePins(cpes: OverviewCpe[]): CpePin[] {
  return cpes.filter(isCpePlaced).map((cpe) => ({
    key: `cpe:${cpe.id}`,
    id: cpe.id,
    lat: cpe.lat,
    lng: cpe.lng,
    shortLabel: cpe.locationName?.trim() || cpe.name,
    label: cpe.name,
    detail: `${cpe.serviceId} · ${cpe.type === "ha" ? "HA" : "Standalone"}`,
    place: [cpe.locationName, cpe.address].map((part) => part?.trim()).filter(Boolean).join(" · ") || null,
    uplinks: cpe.uplinks ?? [],
  }));
}

/** A line from a CPE to the pin of a datacenter its Backhaul lands on. */
export interface CpeLink {
  key: string;
  cpe: string;
  node: string;
  /** The Backhauls (84-) that land there, each once. */
  backhauls: string[];
  /**
   * RTT of the line (CTX-1461): the fastest measured leg of this CPE into the
   * pin's sites. Null when none of its legs is measured; undefined when no
   * Backhaul latency was given.
   */
  rttUs?: number | null;
  /** Just the value, "1.14 ms", only when `rttUs` is a number: the dotted line already says Backhaul. */
  label?: string;
}

/** A leg's RTT by CPE id and the datacenter it lands on; null when not measured. */
export type CpeLegLatencyLookup = (cpeId: number, datacenterId: string) => number | null;

/** The tenant's Backhaul latency, as the map reads it (CTX-1461). */
export interface MapCpeLatency {
  lookup: CpeLegLatencyLookup;
  /** When the IS-IS snapshot was taken, for the legend. */
  measuredAt: string | null;
}

/** The lookup for the legs the backend measured; a CPE with two legs into one site keeps the faster. */
export function buildCpeLegLatencyLookup(legs: OverviewCpeLegLatency[]): CpeLegLatencyLookup {
  const byLeg = new Map<string, number>();
  for (const leg of legs) {
    const key = `${leg.cpeId}|${leg.datacenterId}`;
    const current = byLeg.get(key);
    if (current === undefined || leg.rttUs < current) byLeg.set(key, leg.rttUs);
  }
  return (cpeId, datacenterId) => byLeg.get(`${cpeId}|${datacenterId}`) ?? null;
}

/**
 * One line per CPE and pin its Backhaul lands on. A datacenter that is not
 * on the map (another tenant's site, or one with no GPS) gets no line.
 *
 * With `legLatency` a line also reads the fastest of its measured legs. An
 * unmeasured line keeps no caption, the same rule as the DCI lines.
 */
export function buildCpeLinks(
  pins: CpePin[],
  nodes: TopologyNode[],
  legLatency: CpeLegLatencyLookup | null = null
): CpeLink[] {
  const byNode = nodeBySite(nodes);
  const links = new Map<string, CpeLink>();
  for (const pin of pins) {
    for (const uplink of pin.uplinks) {
      const node = byNode.get(uplink.datacenterId);
      if (!node) continue;
      const key = `${pin.key}|${node}`;
      const link = links.get(key) ?? { key, cpe: pin.key, node, backhauls: [] };
      if (!link.backhauls.includes(uplink.backhaul)) link.backhauls.push(uplink.backhaul);
      if (legLatency) {
        const rttUs = legLatency(pin.id, uplink.datacenterId);
        const current = link.rttUs ?? null;
        link.rttUs = rttUs !== null && (current === null || rttUs < current) ? rttUs : current;
      }
      links.set(key, link);
    }
  }
  return [...links.values()].map((link) =>
    typeof link.rttUs === "number" ? { ...link, label: `${formatDelayMs(link.rttUs)} ms` } : link
  );
}

/** The node a site is drawn in, by site id. */
function nodeBySite(nodes: TopologyNode[]): Map<string, string> {
  return new Map(nodes.flatMap((node) => node.siteIds.map((siteId) => [siteId, node.key] as const)));
}

/**
 * DCI links between nodes. Links inside a metro disappear into its pin; links
 * between the same two nodes add up. "protected" only when every DCI on the
 * line has redundancy.
 *
 * With `latency` (the backbone's site matrix, CTX-843) each line also reads
 * its RTT, from the sites its DCIs join — never from the other sites of a
 * merged pin, which the line does not connect.
 */
export function buildMapLinks(
  links: OverviewSiteLink[],
  nodes: TopologyNode[],
  latency: Pick<MapLatency, "lookup"> | null = null
): MapLink[] {
  const byNode = nodeBySite(nodes);
  const merged = new Map<
    string,
    { from: string; to: string; services: number; redundant: number; sitePairs: [string, string][] }
  >();
  for (const link of links) {
    const a = byNode.get(link.from);
    const b = byNode.get(link.to);
    if (!a || !b || a === b) continue;
    const [from, to] = [a, b].sort();
    const key = `${from}|${to}`;
    const current = merged.get(key) ?? { from, to, services: 0, redundant: 0, sitePairs: [] };
    current.services += link.services;
    current.redundant += link.redundant;
    current.sitePairs.push([link.from, link.to]);
    merged.set(key, current);
  }
  return [...merged.entries()].map(([key, link]) => {
    const count = link.services > 1 ? ` ×${link.services}` : "";
    const protection = link.redundant > 0 && link.redundant === link.services ? " · protected" : "";
    const label = `DCI${count}${protection}`;
    if (!latency) return { key, from: link.from, to: link.to, label };
    const rttUs = lowestRttUs(
      latency.lookup,
      link.sitePairs.map(([a, b]: [string, string]): [string, string] => [latencySiteOf(a), latencySiteOf(b)])
    );
    // An unmeasured line keeps its plain caption: no placeholder to explain.
    const caption = rttUs === null ? label : `${label} · ${formatDelayMs(rttUs)} ms`;
    return { key, from: link.from, to: link.to, label: caption, rttUs };
  });
}

/**
 * One cloud node per cloud, with the nodes whose services reach it. A line takes
 * the state of the cloud services at its site (`cloudHealthBySite`), not the
 * site's overall state: a port down at that site is not the cloud link's fault.
 */
export function buildMapClouds(
  cloudLinks: OverviewCloudLink[],
  nodes: TopologyNode[],
  cloudHealthBySite: Record<string, ServiceHealth> = {}
): MapCloud[] {
  const byNode = nodeBySite(nodes);
  const healthByNode = new Map(
    nodes.map((node) => [node.key, worst(node.siteIds.map((site) => cloudHealthBySite[site] ?? "up"))])
  );
  const clouds = new Map<string, { services: number; from: Set<string> }>();
  for (const link of cloudLinks) {
    const node = byNode.get(link.from);
    if (!node) continue;
    const current = clouds.get(link.cloud) ?? { services: 0, from: new Set<string>() };
    current.services += link.services;
    current.from.add(node);
    clouds.set(link.cloud, current);
  }
  return [...clouds.entries()]
    .sort((a, b) => b[1].services - a[1].services || a[0].localeCompare(b[0]))
    .map(([cloud, { services, from }]) => ({
      cloud,
      services,
      from: [...from].map((node) => ({ node, health: healthByNode.get(node) ?? "up" })),
    }));
}

// ---------------------------------------------------------------------------
// Label placement
// ---------------------------------------------------------------------------

export type LabelSide = "right" | "left" | "below" | "above";

export interface LabelBox {
  key: string;
  /** Pin centre, in map units. */
  x: number;
  y: number;
  /** Card size, in map units. */
  width: number;
  height: number;
  /**
   * Who keeps the room when two cards want it: lower first. The map ranks a
   * site that needs attention, then any other site, then a CPE.
   */
  rank?: number;
}

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * How far to move the box of the scope's sites so they stay on screen; zero
 * when they already are. Panning and zooming use it, so the map can never be
 * dragged off to empty sea with the cloud lines stretching to pins nobody can
 * see.
 *
 * While the sites fit, every one stays at least `margin` inside the edge.
 * Zoomed in past that, the box may be panned across, but `margin` of it always
 * stays on screen.
 */
export function shiftIntoView(box: Rect, area: Rect, margin: number): [number, number] {
  const shift = (low: number, high: number, min: number, max: number) => {
    const fits = high - low <= max - min - 2 * margin;
    if (fits) {
      if (low < min + margin) return min + margin - low;
      if (high > max - margin) return max - margin - high;
      return 0;
    }
    if (high < min + margin) return min + margin - high;
    if (low > max - margin) return max - margin - low;
    return 0;
  };
  return [shift(box.left, box.right, area.left, area.right), shift(box.top, box.bottom, area.top, area.bottom)];
}

/**
 * Gap between a pin's centre and its card, in CSS px: the map draws pins and
 * cards at a fixed pixel size whatever its width, so every clearance below is
 * in px and turned into map units by the rendered scale. Kept in map units,
 * they shrank on a narrow map and cards that "fit" sat on top of each other.
 */
export const LABEL_OFFSET_PX = 22;
/** Radius kept clear around every pin (a 32 px disc), so no card covers another site. */
export const PIN_CLEARANCE_PX = 16;
/**
 * The middle of a pin, the part a card of the same or lower rank may never
 * cover. The full clearance steers which side a card takes; only covering the
 * middle hides it, or KIO MEX2, with Televisa's CPEs in the same building,
 * lost its name to theirs.
 */
const PIN_CORE_PX = 8;
/** Air kept between two cards, so neighbours read as two (Daniel, 2026-10-26). */
const CARD_GAP_PX = 8;
const SIDES: LabelSide[] = ["right", "left", "below", "above"];

/** Where a card sits for a given side, in map units; `offset` is LABEL_OFFSET_PX in map units. */
export function rectFor(box: LabelBox, side: LabelSide, offset: number = LABEL_OFFSET_PX): Rect {
  switch (side) {
    case "right":
      return { left: box.x + offset, right: box.x + offset + box.width, top: box.y - box.height / 2, bottom: box.y + box.height / 2 };
    case "left":
      return { left: box.x - offset - box.width, right: box.x - offset, top: box.y - box.height / 2, bottom: box.y + box.height / 2 };
    case "below":
      return { left: box.x - box.width / 2, right: box.x + box.width / 2, top: box.y + offset, bottom: box.y + offset + box.height };
    case "above":
      return { left: box.x - box.width / 2, right: box.x + box.width / 2, top: box.y - offset - box.height, bottom: box.y - offset };
  }
}

/** The square kept clear around a pin at (x, y). */
export function pinRect([x, y]: [number, number], clearance: number): Rect {
  return { left: x - clearance, right: x + clearance, top: y - clearance, bottom: y + clearance };
}

/** `rect` grown by `by` on every side. */
export function inflateRect(rect: Rect, by: number): Rect {
  return { left: rect.left - by, right: rect.right + by, top: rect.top - by, bottom: rect.bottom + by };
}

function overlap(a: Rect, b: Rect): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

/** Whether `rect` covers any of `others`: a caption with nowhere free to go. */
export function overlapsAny(rect: Rect, others: Rect[]): boolean {
  return others.some((other) => overlap(rect, other) > 0);
}

export interface LabelLayout {
  /** The side of its pin each card sits on; a crowded card opens there on hover. */
  sides: Record<string, LabelSide>;
  /**
   * Cards with no free side: they wait for a hover on their pin, or for a zoom
   * that gives them room, the way a Backhaul RTT does. A cluster of pills
   * touching each other reads as none of them (Daniel, 2026-10-26).
   */
  crowded: Set<string>;
}

/**
 * A side for each pin's card, so cards cover neither each other, nor another
 * pin, nor the edge of the map: sites tens of kilometres apart sit a few pixels
 * apart, and a card always to the right buries the neighbour's (Santa Fe and
 * Toluca). Greedy, by rank and then left to right: each card takes the first
 * free side in right, left, below, above. A card with no free side keeps the
 * one that overlaps least and is set aside as crowded, so it takes no room
 * from the cards after it. `scale` is the map's rendered px per map unit.
 */
export function placeLabels(boxes: LabelBox[], bounds: { width: number; height: number }, scale = 1): LabelLayout {
  const offset = LABEL_OFFSET_PX / scale;
  const gap = CARD_GAP_PX / scale;
  const placed: Rect[] = [];
  const sides: Record<string, LabelSide> = {};
  const crowded = new Set<string>();

  [...boxes]
    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0) || a.x - b.x || a.y - b.y)
    .forEach((box) => {
      // Every pin stays drawn, crowded card or not; the card's own pin is never under it.
      const others = boxes.filter((other) => other.key !== box.key);
      const pins = others.map((other) => pinRect([other.x, other.y], PIN_CLEARANCE_PX / scale));
      // A site's card may graze a CPE's icon in the same building; a CPE's card never a site's pin.
      const cores = others
        .filter((other) => (other.rank ?? 0) <= (box.rank ?? 0))
        .map((other) => pinRect([other.x, other.y], PIN_CORE_PX / scale));
      const onCards = (rect: Rect) => placed.reduce((sum, other) => sum + overlap(inflateRect(rect, gap), other), 0);
      const onto = (rect: Rect, rects: Rect[]) => rects.reduce((sum, other) => sum + overlap(rect, other), 0);
      // Running off the map is worse than any overlap, but is no reason to hide a card.
      const cost = (side: LabelSide) => {
        const rect = rectFor(box, side, offset);
        const outside =
          Math.max(0, -rect.left) + Math.max(0, rect.right - bounds.width) + Math.max(0, -rect.top) + Math.max(0, rect.bottom - bounds.height);
        return onCards(rect) + onto(rect, pins) + outside * box.height * 4;
      };
      const best = SIDES.reduce((winner, side) => (cost(side) < cost(winner) ? side : winner), SIDES[0]);
      const rect = rectFor(box, best, offset);
      sides[box.key] = best;
      if (onCards(rect) > 0 || onto(rect, cores) > 0) crowded.add(box.key);
      else placed.push(rect);
    });
  return { sides, crowded };
}

// ---------------------------------------------------------------------------
// Cloud node placement
// ---------------------------------------------------------------------------

/**
 * The height of each cloud node in the right-hand column, chosen so lines do
 * not cross: each cloud sits at the average height of the sites that reach it
 * (its barycentre), clouds keep that order top to bottom, and they are pushed
 * apart only as far as `minGap` requires, inside `[top, bottom]`. Ordering by
 * service count instead sent Querétaro's line down to Azure across Mexico
 * City's line up to AWS.
 */
export function layoutCloudColumn(
  clouds: MapCloud[],
  heightOf: (node: string) => number,
  { top, bottom, minGap }: { top: number; bottom: number; minGap: number }
): Record<string, number> {
  const wanted = clouds
    .map((cloud) => ({
      cloud: cloud.cloud,
      y: cloud.from.reduce((sum, source) => sum + heightOf(source.node), 0) / Math.max(cloud.from.length, 1),
    }))
    .sort((a, b) => a.y - b.y || a.cloud.localeCompare(b.cloud));

  // Top-down: keep the gap; bottom-up: stay inside the column; then the top again.
  const ys = wanted.map((entry) => Math.min(Math.max(entry.y, top), bottom));
  for (let i = 1; i < ys.length; i++) ys[i] = Math.max(ys[i], ys[i - 1] + minGap);
  if (ys.length > 0) ys[ys.length - 1] = Math.min(ys[ys.length - 1], bottom);
  for (let i = ys.length - 2; i >= 0; i--) ys[i] = Math.min(ys[i], ys[i + 1] - minGap);
  for (let i = 0; i < ys.length; i++) ys[i] = Math.max(ys[i], top + i * minGap);

  return Object.fromEntries(wanted.map((entry, index) => [entry.cloud, ys[index]]));
}

// ---------------------------------------------------------------------------
// Link caption placement
// ---------------------------------------------------------------------------

/** Distances tried, along the line's normal, for a caption that collides at the midpoint. */
const CAPTION_OFFSETS = [0, 24, -24, 40, -40, 58, -58, 78, -78];

/**
 * Where a link's caption ("DCI · protected") goes: the midpoint of the line, or
 * the nearest spot along its normal that covers no pin and no card. Two sites
 * ~35 km apart put the midpoint under their own pins, which is how "DCI" went
 * unreadable at the fitted zoom.
 */
export function placeLinkCaption(
  mid: [number, number],
  normal: [number, number],
  size: { width: number; height: number },
  obstacles: Rect[],
  bounds: { width: number; height: number }
): { x: number; y: number; offset: number } {
  const rectAt = (offset: number): Rect => {
    const x = mid[0] + normal[0] * offset;
    const y = mid[1] + normal[1] * offset;
    return { left: x - size.width / 2, right: x + size.width / 2, top: y - size.height / 2, bottom: y + size.height / 2 };
  };
  const cost = (offset: number) => {
    const rect = rectAt(offset);
    const outside =
      Math.max(0, -rect.left) + Math.max(0, rect.right - bounds.width) + Math.max(0, -rect.top) + Math.max(0, rect.bottom - bounds.height);
    return obstacles.reduce((sum, other) => sum + overlap(rect, other), 0) + outside * size.height * 4;
  };
  const best = CAPTION_OFFSETS.reduce((winner, offset) => (cost(offset) < cost(winner) ? offset : winner), 0);
  return { x: mid[0] + normal[0] * best, y: mid[1] + normal[1] * best, offset: best };
}
